import { Inject, Injectable } from '@angular/core';
import {
  ActionsModel,
  CanPipe,
  ClientEnvironment,
  ENVIRONMENT,
  FieldType,
  FormControlModel,
  StepperGroupModel,
  StepperModel,
  WidthClass,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, UserInfo } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { customFields } from '../../../customer/customer-asset-fields';
import { lufthansaGeneralInformationFields } from '../../../customer/lufthansa/lufthansa-form-control.model';
import { vkbGeneralInformationFields, vkbUsageRightsFields } from '../../../customer/vkb/vkb-form-fields';
import { vorwerkGeneralInformationFields, vorwerkTaggingNTaxonomyFields } from '../../../customer/vorwerk/vorwerk-form-control.model';
import { StepperConfig } from '../../../models/stepper-config.interface';
import {
  seatContentDescriptionFields,
  seatGeneralInformationFields,
  seatTaggingNTaxonomyFields,
  seatUsageRightsFields,
} from './../../../customer/seat/seat-form-fields';
import {
  contentDescriptionConfigs,
  generalInformation,
  lufthansaTagFields,
  productionDetailsStepperConfigs,
  tags,
} from './../../asset-upload/stepper-config/asset-settings-stepper.constants';
@Injectable({
  providedIn: 'root',
})
export class AssetEditStepperConfigService {
  existingCollectionOption = {
    id: 'add2ExistingCollection',
    name: $localize`Add to existing collection`,
  };

  AnyAsset = anyAsset;

  saveIcon = '';

  constructor(
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
    private canPipe: CanPipe,
  ) {
    if ((CustomClients.Vorwerk, CustomClients.VKB, CustomClients.Standard).includes(env.brandingKey)) {
      this.saveIcon = 'save';
    }
  }

  /**
   * This function is used to create the stepper config for the asset upload component.
   */
  getAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    const nextActionObj: ActionsModel = {
      actionLabel: $localize`:action:Save`,
      actionType: 'uploadAsset',
      actionTypePosition: 'right',
      icon: this.saveIcon,
      alignIcon: 'left',
      disable: false,
      visible: true,
    };
    const tagsModel = tags(stepperConfig, nextActionObj);
    if ([CustomClients.LufthansaMedialounge, CustomClients.LufthansaMediacenter].includes(this.env.brandingKey)) {
      tagsModel.formFields?.push(...lufthansaTagFields);
    }
    const uploadStepperMetaDataConfig: StepperModel = {
      stepID: 'assetUpload',
      stepLabel: $localize`Edit metadata`,
      stepper: [{ ...generalInformation() }, { ...tagsModel }],
    };
    const uploadStepperUsageRightsConfig: StepperModel = {
      stepID: 'assetUpload',
      stepLabel: $localize`Edit usage rights`,
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`Usage rights`,
          stepperGroupName: 'usageRight',
          selected: true,
          visited: false,
          disable: true,
          role: 'usagerights',
          formFields: [
            {
              formTypeId: '1',
              formFieldName: 'owner',
              fieldType: FieldType.Dropdown,
              fieldLabel: $localize`Owner`,
              placeholder: $localize`:placeholder:Owner`,
              widthClass: WidthClass.WFull,
              options: users,
              value: undefined,
              dropDownSearch: true,
              validators: {
                required: true,
              },
              visible: true,
            },
            {
              formTypeId: '2',
              formFieldName: 'expiryDate',
              fieldType: FieldType.Date,
              fieldLabel: $localize`Expiry date`,
              placeholder: $localize`DD/MM/YYYY`,
              minDate: new Date(),
              widthClass: WidthClass.W12,
              value: '',
              validators: {
                required: true,
              },
              visible: true,
            },
            {
              formTypeId: '3',
              formFieldName: 'noExpiry',
              fieldType: FieldType.Checkbox,
              fieldLabel: $localize`No Expiry date`,
              placeholder: '',
              widthClass: WidthClass.W12,
              value: '',
              validators: {
                required: false,
              },
              visible: true,
            },
            {
              formTypeId: '4',
              formFieldName: 'notes',
              fieldType: FieldType.Textarea,
              fieldLabel: $localize`Source and notes`,
              placeholder: $localize`:placeholder:Source and notes`,
              inputType: 'text',
              widthClass: WidthClass.WFull,
              value: '',
              visible: true,
            },
          ],
          actions: [
            {
              actionLabel: $localize`:action:Save`,
              actionType: 'uploadAsset',
              actionTypePosition: 'right',
              icon: this.saveIcon,
              alignIcon: 'left',
              disable: false,
              visible: true,
            },
          ],
        },
      ],
    };
    const uploadStepperRelationConfig: StepperModel = {
      stepID: 'assetUpload',
      stepLabel: $localize`Edit relations`,
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`Relations: Select assets`,
          stepperGroupName: 'selectAssets',
          overrideHeader: $localize`Relations: Select assets to set a relation with uploaded assets`,
          selected: false,
          visited: false,
          disable: false,
          role: 'relations',
          formFields: [
            {
              formTypeId: '1',
              formFieldName: 'assetGallery',
              fieldType: FieldType.AssetGallery,
              fieldLabel: $localize`Select Assets`,
              placeholder: $localize`:placeholder:Select Assets`,
              widthClass: WidthClass.WFull,
              visible: true,
              value: [],
            },
          ],
          actions: [
            {
              actionLabel: $localize`:action:Next`,
              goToStep: 2,
              actionType: 'selectAssets',
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              disable: false,
              visible: true,
            },
          ],
        },
        {
          stepperId: '2',
          stepperName: $localize`Relations: Define relation type`,
          stepperGroupName: 'defineAssetRelation',
          selected: false,
          visited: false,
          disable: true,
          role: 'relations',
          formFields: [
            {
              formTypeId: '1',
              formFieldName: 'assetRelation',
              fieldType: FieldType.AssetRelation,
              fieldLabel: $localize`Define asset relation`,
              placeholder: $localize`:placeholder:Define asset relation`,
              widthClass: WidthClass.WFull,
              visible: true,
              value: [],
            },
          ],
          actions: [
            {
              actionLabel: $localize`:action:Back`,
              actionType: 'back',
              goToStep: 1,
              actionTypePosition: 'left',
              icon: 'chevron_left',
              alignIcon: 'left',
              visible: true,
            },
            {
              actionLabel: $localize`:action:Save`,
              actionType: 'uploadAsset',
              actionTypePosition: 'right',
              icon: this.saveIcon,
              alignIcon: 'left',
              disable: false,
              visible: true,
            },
          ],
        },
      ],
    };

    if (this.env.brandingKey === CustomClients.Standard) {
      uploadStepperMetaDataConfig.stepper.splice(2, 0, productionDetailsStepperConfigs());
      const actions = uploadStepperMetaDataConfig.stepper[1].actions;
      uploadStepperMetaDataConfig.stepper[1].actions = uploadStepperMetaDataConfig.stepper[2].actions;
      uploadStepperMetaDataConfig.stepper[2].actions = actions;
    }

    if (CustomClients.WestLotto === this.env.brandingKey) {
      const tagStepperForm = this.findStepperForm(uploadStepperMetaDataConfig.stepper, 'tags');
      // Get the westLotto tags custom fields
      const westLottoTagsCustomFormFields: FormControlModel[] = JSON.parse(JSON.stringify(customFields.get(CustomClients.WestLotto)));
      // Set widthClass of the FormFields
      westLottoTagsCustomFormFields.forEach((field) => {
        field.widthClass = WidthClass.W12;
      });
      // push the westLotto tags custom fields to standard tag stepper
      tagStepperForm?.push(...westLottoTagsCustomFormFields);
    }
    this.updateStepperActionGoToStep(uploadStepperMetaDataConfig);

    const stepperConfigObject: AnyType = {
      metaData: uploadStepperMetaDataConfig,
      usageRights: uploadStepperUsageRightsConfig,
      relations: uploadStepperRelationConfig,
    };
    let uploadStepperConfig: AnyType = {};
    if (stepperType) {
      uploadStepperConfig = stepperConfigObject[stepperType];
    }
    return uploadStepperConfig;
  };

  /**
   * This function is used to create the stepper config for Lufthansa customer for the asset upload component.
   */
  getLufthansaAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getAssetSettingsStepperConfig(users, stepperConfig, stepperType);
    // Deep copy of the standard configuration
    let lufthansaAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepper = this.findStepper(lufthansaAssetsStepperConfig.stepper, 'generalInformation');
    // Hide 'agency', 'client', 'brand', fields
    if (generalInformationStepper) generalInformationStepper.formFields = lufthansaGeneralInformationFields();
    generalInformationStepper?.formFields?.forEach((formField) => {
      if (['agency', 'client'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
      if (formField.accessibilityRole && !this.canPipe.transform(this.AnyAsset, formField.accessibilityRole)) {
        formField.visible = false;
      }
    });

    /* Tags & taxonomy update */
    /************************/

    // hide relation step for arval
    lufthansaAssetsStepperConfig.stepper = lufthansaAssetsStepperConfig.stepper.filter(
      (step) => !['usageRight', 'selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName),
    );
    // return only those stepper configs in which user is allowed
    lufthansaAssetsStepperConfig = this.stepperAccessibility(lufthansaAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(lufthansaAssetsStepperConfig);
    // return arval stepper config
    return lufthansaAssetsStepperConfig;
  };

  /**
   * This function is used to create the stepper config for Lufthansa customer for the asset upload component.
   */
  getVKBAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getAssetSettingsStepperConfig(users, stepperConfig, stepperType);
    // Deep copy of the standard configuration
    let vkbAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepper = this.findStepper(vkbAssetsStepperConfig.stepper, 'generalInformation');
    // Hide 'agency', 'client', 'brand', fields
    if (generalInformationStepper) generalInformationStepper.formFields = vkbGeneralInformationFields();
    generalInformationStepper?.formFields?.forEach((formField) => {
      if (['agency', 'client', 'brand'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
    });
    /* Tags & taxonomy update */
    /************************/
    const tagStepperForm: AnyType = this.findStepperForm(vkbAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    const tagFormField = tagStepperForm?.findIndex((field: AnyType) => field.formFieldName === AssetMetadata.General.TAGS) ?? -1;
    if (tagFormField != -1) {
      tagStepperForm[tagFormField].fieldLabel = $localize`Tags/Schlagworte`;
      tagStepperForm[tagFormField].infotext =
        'Als Tags/Schlagworte können z. B. beschreibende Elemente der Assets, das Produkt, Produktgruppe, für die das Asset verwendet wird, angegeben werden. Je mehr beschreibende Tags, umso besser. Bitte Groß-/Kleinschreibung beim Anlegen neuer Schlagworte beachten! Schreibfehler kann man selbst korrigieren oder den Tag wieder löschen. Um einen neuen Tag hinzuzufügen, geben Sie diesen in der "Suche" (Pfeil nach unten) ein und bestätigen Sie die Eingabe durch drücken der "Enter"-Taste.';
      tagStepperForm[tagFormField].mandatory = true;
      if (!tagStepperForm[tagFormField].validators) {
        tagStepperForm[tagFormField].validators = {
          required: true,
          minLength: 6,
        };
      }
    }

    /* Usage Rights update */
    /************************/
    // Get the usage rights stepper forms
    const usageRightsStepperForm = this.findStepperForm(vkbAssetsStepperConfig.stepper, 'usageRight');
    // replace usage right form
    if (usageRightsStepperForm) usageRightsStepperForm?.splice(0, usageRightsStepperForm.length, ...vkbUsageRightsFields());

    // hide relation step for lufthansa
    vkbAssetsStepperConfig.stepper = vkbAssetsStepperConfig.stepper.filter((step) => !['selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName));

    // return only those stepper configs in which user is allowed
    vkbAssetsStepperConfig = this.stepperAccessibility(vkbAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(vkbAssetsStepperConfig);
    // return arval stepper config
    return vkbAssetsStepperConfig;
  };

  /**
   * This function is used to create the stepper config for Lufthansa customer for the asset upload component.
   */
  getSeatAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getAssetSettingsStepperConfig(users, stepperConfig, stepperType);
    // Deep copy of the standard configuration
    let seatAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepper = this.findStepper(seatAssetsStepperConfig.stepper, 'generalInformation');
    // Hide 'agency' field
    if (generalInformationStepper) generalInformationStepper.formFields = seatGeneralInformationFields();
    generalInformationStepper?.formFields?.forEach((formField) => {
      if (['agency'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
    });

    /* Content description*/
    /************************/
    if (stepperType == 'metaData') {
      seatAssetsStepperConfig.stepper.splice(1, 0, contentDescriptionConfigs());
      const contentDescriptionStepperForm = this.findStepperForm(seatAssetsStepperConfig.stepper, 'contentDescription');
      if (contentDescriptionStepperForm) contentDescriptionStepperForm?.splice(0, contentDescriptionStepperForm.length, ...seatContentDescriptionFields());
    }

    /* Tags & taxonomy update */
    /************************/
    const tagStepper = this.findStepper(seatAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepper) {
      tagStepper.stepperId = '3';
    }
    const tagStepperForm: AnyType = this.findStepperForm(seatAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepperForm) tagStepperForm.push(...seatTaggingNTaxonomyFields());

    /* Usage Rights update */
    /************************/
    // Get the usage rights stepper forms
    const usageRightsStepperForm = this.findStepperForm(seatAssetsStepperConfig.stepper, 'usageRight');
    // replace usage right form
    if (usageRightsStepperForm) usageRightsStepperForm?.splice(0, usageRightsStepperForm.length, ...seatUsageRightsFields());

    // hide relation step for lufthansa
    seatAssetsStepperConfig.stepper = seatAssetsStepperConfig.stepper.filter(
      (step) => !['selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName),
    );

    // return only those stepper configs in which user is allowed
    seatAssetsStepperConfig = this.stepperAccessibility(seatAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(seatAssetsStepperConfig);
    // return arval stepper config
    return seatAssetsStepperConfig;
  };

  /**
   * This function is used to create the stepper config for Arval customer for the asset upload component.
   */
  getArvalAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getAssetSettingsStepperConfig(users, stepperConfig, stepperType);
    // Deep copy of the standard configuration
    let arvalAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, 'generalInformation');
    // Hide 'agency', 'client', 'brand', fields
    generalInformationStepperForm?.forEach((formField) => {
      if (['agency', 'client', 'brand', 'publishStartDate', 'publishEndDate'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
      if (['status'].includes(formField.formFieldName || '')) {
        formField.options = [
          {
            id: Asset.StatusEnum.Pending,
            name: $localize`Pending`,
          },
        ];
      }
    });
    /* Tags & taxonomy update */
    // Get the tags stepper forms
    const tagStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    // Get the arval tags custom fields
    const arvalTagsCustomFormFields: FormControlModel[] = JSON.parse(JSON.stringify(customFields.get(CustomClients.Arval)));
    // Set widthClass of the FormFields
    arvalTagsCustomFormFields.forEach((field) => {
      field.widthClass = WidthClass.W12;
    });
    // push the arval tags custom fields to standard tag stepper
    tagStepperForm?.push(...arvalTagsCustomFormFields);
    /************************/

    /* Usage rights update */
    // Get the usage rights stepper forms
    const usageRightsStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, 'usageRight');
    // Get Arval specific usage right forms
    const arvalUsageRightCustomFormFields = this.arvalUsageRightsFields();
    // insert after owner; the arval usage rights custom fields to standard usage rights stepper
    usageRightsStepperForm?.splice(1, 0, ...arvalUsageRightCustomFormFields);
    /************************/

    // hide relation step for arval
    arvalAssetsStepperConfig.stepper = arvalAssetsStepperConfig.stepper.filter(
      (step) => !['selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName),
    );
    // return only those stepper configs in which user is allowed
    arvalAssetsStepperConfig = this.stepperAccessibility(arvalAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(arvalAssetsStepperConfig);
    // return arval stepper config
    return arvalAssetsStepperConfig;
  };

  findStepper = (stepper: StepperGroupModel[], indexKey: string) => {
    const stepperGroupIndex = stepper.findIndex((step) => step.stepperGroupName === indexKey);
    return stepperGroupIndex !== -1 ? stepper[stepperGroupIndex] : undefined;
  };

  /**
   * This function is used to create the stepper config for vorwerk customer for the asset upload component.
   */
  getVorwerkAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, stepperType?: string): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getAssetSettingsStepperConfig(users, stepperConfig, stepperType);
    // Deep copy of the standard configuration
    const vorwerkAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    /* General Information update */
    const generalInformationStepper = this.findStepper(vorwerkAssetsStepperConfig.stepper, 'generalInformation');
    if (generalInformationStepper) generalInformationStepper.formFields = vorwerkGeneralInformationFields();
    /************************/

    /* Tags & taxonomy update */
    const tagStepperForm = this.findStepperForm(vorwerkAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepperForm) tagStepperForm.push(...vorwerkTaggingNTaxonomyFields());
    /************************/

    return vorwerkAssetsStepperConfig;
  };

  /**
   * Find stepper forms based on the key provided
   * @param stepper
   * @param indexKey
   * @returns
   */
  private findStepperForm = (stepper: StepperGroupModel[], indexKey: string) => {
    const stepperGroupIndex = stepper.findIndex((step) => step.stepperGroupName === indexKey);
    return stepperGroupIndex !== -1 ? stepper[stepperGroupIndex].formFields : [];
  };

  /**
   * Arval usage rights forms
   * @returns
   */
  private arvalUsageRightsFields = (): FormControlModel[] => [
    {
      formTypeId: '1',
      formFieldName: 'copyright',
      fieldType: 'text',
      fieldLabel: $localize`Copyright`,
      placeholder: $localize`:placeholder:Copyright`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
    {
      formTypeId: '2',
      formFieldName: 'country',
      fieldType: 'text',
      fieldLabel: $localize`Country`,
      placeholder: $localize`:placeholder:Country`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
    {
      formTypeId: '3',
      formFieldName: 'provider',
      fieldType: 'text',
      fieldLabel: $localize`Provider`,
      placeholder: $localize`:placeholder:Provider`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
    {
      formTypeId: '4',
      formFieldName: 'source',
      fieldType: 'text',
      fieldLabel: $localize`Source`,
      placeholder: $localize`Source`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
  ];

  /**
   * Sequence stepper id if any stepper is hidden due to user accessibility
   * @param uploadStepperConfig
   */
  private updateStepperActionGoToStep(uploadStepperConfig: StepperModel): void {
    // re-sequence the stepperId in stepper config;
    uploadStepperConfig.stepper.forEach((step, i) => (step.stepperId = (i + 1).toString()));
    // update the stepper config's GoToStep value for next and back button
    uploadStepperConfig.stepper.forEach((step, i) => {
      step.actions?.forEach((action) => {
        if (action.actionType === 'next') {
          action.goToStep = uploadStepperConfig.stepper[i + 1]?.stepperId ? +uploadStepperConfig.stepper[i + 1].stepperId : action.goToStep;
        }
        if (action.actionType === 'back') {
          action.goToStep = uploadStepperConfig.stepper[i - 1]?.stepperId ? +uploadStepperConfig.stepper[i - 1].stepperId : action.goToStep;
        }
      });
    });
  }

  /**
   * Check stepper role property if user is allowed to see certain steps
   * @param uploadStepperConfig
   * @returns
   */
  private stepperAccessibility(uploadStepperConfig: StepperModel): StepperModel {
    uploadStepperConfig.stepper = uploadStepperConfig.stepper.filter((step) => this.canPipe.transform(this.AnyAsset, step.role || 'upload'));
    return uploadStepperConfig;
  }
}
